import React, { useEffect, useState } from "react";
import styles from "./Login.module.scss";
import { PasswordInput, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import ApiService from "../../services/api";
import fireToast from "../../helpers/toaster";
import { useNavigate } from "react-router-dom";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import { PATHS } from "../../constants/paths";
import { ROLES } from "../../constants/roles";
import { setUserCookies } from "../../helpers/cookies";
import { persistUserProfile } from "../../helpers/userProfileStorage";

const getPathByRole = (role) => {
  switch (role) {
    case ROLES.RECEPTIONIST:
      return PATHS.RECEPTIONIST_DASHBOARD;
    case ROLES.PRE_OPD_MANAGEMENT:
      return PATHS.PRE_OPD_ASSESSMENTS;
    case ROLES.DOCTOR:
      return PATHS.DOCTOR_DASHBOARD;
    case ROLES.FOLLOW_UP_MANAGEMENT:
      return PATHS.FOLLOW_UP_LIST;
    case ROLES.COUNSELLOR:
      return PATHS.COUNSELLOR_DASHBOARD;
    case ROLES.NURSE:
      return PATHS.NURSE_DASHBOARD;
    case ROLES.IPD_MANAGEMENT:
      return PATHS.IPD_DASHBOARD;
    case ROLES.PHARMACIST:
      return PATHS.PHARMACIST_DASHBOARD;
    case ROLES.RADIOLOGIST:
      return PATHS.RADIOLOGIST_DASHBOARD;
    case ROLES.LAB_TEST:
      return PATHS.LAB_TEST_DASHBOARD;
    case ROLES.THERAPIST:
      return PATHS.THERAPIST_DASHBOARD;
    case ROLES.YOGA:
      return PATHS.YOGA_DASHBOARD;
    case ROLES.BILLING_MANAGER:
      return PATHS.BILLING_MANAGER_DASHBOARD;
    case ROLES.DIETICIAN:
      return PATHS.DIETICIAN_DASHBOARD;
    case ROLES.STAFF_MANAGER:
      return PATHS.STAFF_MANAGER_STAFF;
    case ROLES.STORE_MANAGER:
      return PATHS.STORE_MANAGER_DASHBOARD;
    case ROLES.LOGISTICS:
      return PATHS.LOGISTICS;
    case ROLES.ADMIN:
      return PATHS.ADMIN_DASHBOARD;
    default:
      return null;
  }
};

const Login = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const form = useForm({
    initialValues: {
      email: "",
      password: "",
    },

    validate: {
      email: (value) => (/^\S+@\S+\.\S+$/.test(value) ? null : "Invalid email"),
      password: (value) => (value.length >= 3 ? null : "Invalid password"),
    },
  });

  const handleSubmit = (values) => {
    setLoading(true);

    ApiService.login(values)
      .then((response) => {
        localStorage.setItem("user_token", response.data.token);
        setUserCookies(response.data.user);
        persistUserProfile(response.data.user);
        const loginRoles =
          response.data.user.tokenRoles || response.data.user.role || [];
        if (loginRoles.length > 1) {
          navigate(PATHS.ROLES, { state: { roles: loginRoles } });
        } else {
          const singleRole = loginRoles[0];
          localStorage.setItem("user_role", singleRole);
          const path = getPathByRole(singleRole);
          if (path) navigate(path);
        }
      })
      .catch((error) => {
        if (error.response) {
          setLoading(false);
          fireToast("error", error.response.data.message, "bottom-right", true);
        }
      });
  };

  useEffect(() => {
    const token = localStorage.getItem("user_token");
    const role = localStorage.getItem("user_role");
    if (token) {
      const path = getPathByRole(role);
      if (path) navigate(path);
    }
  }, []);

  return (
    <>
      <div className={styles.heading_container}>
        <p className={styles.heading}>Sign In</p>
        <p className={styles.text}>Sign in to access Admin Panel</p>
      </div>

      <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
        <TextInput
          label="Email"
          placeholder="Enter email address"
          size="md"
          required
          {...form.getInputProps("email")}
        />
        <div className={styles.password_block}>
          <PasswordInput
            label="Password"
            placeholder="Enter password"
            size="md"
            required
            {...form.getInputProps("password")}
          />
          <button
            type="button"
            className={styles.forgot_link}
            onClick={() =>
              navigate(PATHS.FORGOT_PASSWORD, {
                state: { email: form.values.email.trim() },
              })
            }
          >
            Forgot password?
          </button>
        </div>

        <PrimaryButton text="Sign In" type="submit" loading={loading} />
      </form>
    </>
  );
};

export default Login;
